import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type {
  FerryIngredient,
  FlightIngredient,
  HotelIngredient,
  RecipeMainIngredient,
  StayIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  isFlightIngredient,
  isHotelIngredient,
  isOwnStayIngredient,
  isOwnTransportIngredient,
  isStayIngredient,
  isBookableTransportIngredient,
  isUnbookableTransportIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import type { OwnTransportIngredient } from '@apps/main/modules/recipe/ingredients/ownTransportIngredient';
import {
  getActiveFilters,
  getHotelFilterFromIngredient,
} from '@apps/main/new-desktop/modules/hotelFilters';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import type { CarrierName, IngredientId } from '@libs/trip-planner-interfaces';
import {
  AvailabilityOrder,
  SearchTermType,
  TransportType,
} from '@libs/trip-planner-interfaces';

import type {
  ItineraryAccommodationUpsellLoaderSegment,
  ItineraryFlightLoaderSegment,
  ItineraryLoaderSegment,
  ItineraryNoTransportLoaderSegment,
  ItineraryOwnTransportLoaderSegment,
  ItinerarySimpleTransportLoaderSegment,
  ItineraryUpsellNoTransportSegment,
  LocationSegment,
} from '../models';
import { ItinerarySegmentType, ProductCardTopBarType } from '../models';

import {
  getCityText,
  getCountryText,
  getLocationSegment,
} from './tripOverviewSegments/tripOverviewLocationSegmentsLogic';
import { getItinerarySegmentTypeFromTransportType } from './tripOverviewSegments/tripOverviewSimpleTransportSegmentsLogic';
import {
  getFerryFilterPills,
  getFlightFilterPills,
} from './tripOverviewSegments/tripOverviewTransportFiltersPillsLogic';

interface GetHotelLoaderSegmentsParams {
  recipe: RecipeMainIngredient[];
  ingredient: HotelIngredient;
  ingredientIndex: number;
  isSmallDevice: boolean;
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[];
  currency: string;
}

interface GetOwnTransportLoaderSegmentsParams {
  recipe: RecipeMainIngredient[];
  ingredient: OwnTransportIngredient;
  ingredientIndex: number;
  hasUserSelectedTransport: boolean;
}

const { t } = i18n.global;

export function getTripItineraryLoaderSegments(
  recipe: RecipeMainIngredient[],
  isGeolocationAvailable: boolean,
  isSmallDevice: boolean,
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  currency: string,
  hasUserSelectedTransport: boolean
): ItineraryLoaderSegment[] {
  const loaderSegments: ItineraryLoaderSegment[] = [];
  const firstIngredient = recipe[0];

  if (
    isGeolocationAvailable &&
    firstIngredient &&
    isOwnTransportIngredient(firstIngredient) &&
    firstIngredient.ownTransportType !== TransportType.None
  ) {
    loaderSegments.push({
      segmentType: ItinerarySegmentType.StartLocation,
      location: { name: t('planner.your_position') },
    });
  }

  const recipeLoaders = recipe.flatMap(
    (ingredient: RecipeMainIngredient, index: number) => {
      const ingredientLoaders: ItineraryLoaderSegment[] = [];

      if (isHotelIngredient(ingredient)) {
        const hotelLoadersSementParams: GetHotelLoaderSegmentsParams = {
          recipe,
          ingredient,
          ingredientIndex: index,
          isSmallDevice,
          getFlightAllCarriers,
          getFerryAllCarriers,
          currency,
        };

        const hotelLoaders = getHotelLoaderSegments(hotelLoadersSementParams);
        ingredientLoaders.push(...hotelLoaders);
      }

      if (isOwnTransportIngredient(ingredient)) {
        const ownTransportLoaderSementParams: GetOwnTransportLoaderSegmentsParams =
          {
            recipe,
            ingredient,
            ingredientIndex: index,
            hasUserSelectedTransport,
          };

        const ownTransportLoader = getOwnTransportLoaderSegment(
          ownTransportLoaderSementParams
        );
        if (ownTransportLoader) {
          ingredientLoaders.push(ownTransportLoader);
        }
      }

      if (isOwnStayIngredient(ingredient)) {
        const ownStayLoaders = getOwnStayLoaderSegments(
          recipe,
          ingredient,
          index,
          getFlightAllCarriers,
          getFerryAllCarriers,
          currency
        );
        ingredientLoaders.push(...ownStayLoaders);
      }

      const isLastIngredientOnlyBookableTransport =
        isBookableTransportIngredient(ingredient) &&
        index === recipe.length - 1;

      if (isLastIngredientOnlyBookableTransport) {
        const productCardTopBarType = getTransportProductTopBarType(ingredient);

        let suggestionType: SuggestionType | undefined = undefined;
        if (productCardTopBarType !== ProductCardTopBarType.Selected) {
          suggestionType =
            productCardTopBarType === ProductCardTopBarType.Suggestion
              ? SuggestionType.MostPopular
              : SuggestionType.Cheapest;
        }

        if (isFlightIngredient(ingredient)) {
          const filtersPills = getFlightFilterPills(
            ingredient.filters,
            getFlightAllCarriers(ingredient.ingredientId),
            currency
          );

          ingredientLoaders.push({
            segmentType: ItinerarySegmentType.Flight,
            ingredientId: ingredient.ingredientId,
            isLastSegment: true,
            barType: productCardTopBarType,
            filtersPills,
            suggestionType,
          });
        } else {
          const filtersPills = getFerryFilterPills(
            ingredient.filters,
            getFerryAllCarriers(ingredient.ingredientId)
          );

          ingredientLoaders.push({
            segmentType: ItinerarySegmentType.Ferry,
            ingredientId: ingredient.ingredientId,
            isLastSegment: true,
            barType: productCardTopBarType,
            filtersPills,
            suggestionType,
          });
        }
      }
      return ingredientLoaders;
    }
  );

  loaderSegments.push(...recipeLoaders);

  const lastRecipeIngredient = recipe[recipe.length - 1];

  if (
    lastRecipeIngredient &&
    isOwnTransportIngredient(lastRecipeIngredient) &&
    lastRecipeIngredient.ownTransportType !== TransportType.None
  ) {
    loaderSegments.push({
      segmentType: ItinerarySegmentType.EndLocation,
      location: { name: t('planner.your_position') },
    });
  }

  return loaderSegments;
}

function getTransportProductTopBarType(
  ingerdient: FlightIngredient | FerryIngredient
): ProductCardTopBarType | undefined {
  if (ingerdient.transportIds?.length) {
    return undefined;
  }

  if (ingerdient.availabilityOrder === AvailabilityOrder.Popular) {
    return ProductCardTopBarType.Suggestion;
  } else {
    return ProductCardTopBarType.Cheapest;
  }
}

function getHotelProductTopBarType(
  ingerdient: HotelIngredient
): ProductCardTopBarType {
  if (ingerdient.heartedHotels?.length) {
    return ProductCardTopBarType.Selected;
  }

  return ingerdient.availabilityOrder === AvailabilityOrder.Popular
    ? ProductCardTopBarType.Suggestion
    : ProductCardTopBarType.Cheapest;
}

function getHotelLoaderSegments(
  params: GetHotelLoaderSegmentsParams
): ItineraryLoaderSegment[] {
  const ingredientLoaders: ItineraryLoaderSegment[] = [];

  const previousIngredient: RecipeMainIngredient | undefined =
    params.recipe[params.ingredientIndex - 1];

  ingredientLoaders.push(
    ...getBookableTransportLoaderSegments(
      previousIngredient,
      params.getFlightAllCarriers,
      params.getFerryAllCarriers,
      params.currency
    )
  );

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.DateLocation,
    location: getHotelDateLocationLoaderLocationSegment(params.ingredient),
    numberOfNights: params.ingredient.numberOfNights,
    ingredientId: params.ingredient.ingredientId,
  });

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.Accommodation,
    barType: getHotelProductTopBarType(params.ingredient),
    ingredientId: params.ingredient.ingredientId,
    ingredientIndex: params.ingredientIndex,
    filters: params.ingredient.filters
      ? getActiveFilters(
          getHotelFilterFromIngredient(params.ingredient.filters),
          params.ingredient.neighborhoodPolygons
        )
      : [],
  });

  params.ingredient.subIngredients.forEach((subIngredient) =>
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.Activity,
      barType: undefined,
      ingredientId: subIngredient.ingredientId,
      ingredientIndex: params.ingredientIndex,
      destination: getCityText(params.ingredient.displayText),
    })
  );
  if (
    !ingredientLoaders.some(
      (loader) => loader.segmentType === ItinerarySegmentType.Activity
    )
  ) {
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.Upsell,
      ingredientId: params.ingredient.ingredientId,
      destination: getCityText(params.ingredient.displayText),
    });
  }

  return ingredientLoaders;
}

function getOwnTransportLoaderSegment(
  params: GetOwnTransportLoaderSegmentsParams
): ItineraryOwnTransportLoaderSegment | undefined {
  const previousIngredient: RecipeMainIngredient | undefined =
    params.recipe[params.ingredientIndex - 1];
  const nextIngredient: RecipeMainIngredient | undefined =
    params.recipe[params.ingredientIndex + 1];
  if (
    params.ingredientIndex === 0 &&
    params.ingredient.ownTransportType === TransportType.None
  ) {
    if (nextIngredient && isStayIngredient(nextIngredient)) {
      return getTransportUpsellLoaderSegments(
        nextIngredient,
        params.ingredient
      );
    }
  } else {
    const nextIngredientDestination =
      nextIngredient && isStayIngredient(nextIngredient)
        ? nextIngredient.displayText
        : undefined;
    return getSimpleTransportLoaderSegments(
      params.ingredient,
      previousIngredient,
      nextIngredientDestination,
      params.recipe
    );
  }
}

function getOwnStayLoaderSegments(
  recipe: RecipeMainIngredient[],
  ingredient: OwnStayIngredient,
  ingredientIndex: number,
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  currency: string
): ItineraryLoaderSegment[] {
  const ingredientLoaders: ItineraryLoaderSegment[] = [];

  const previousIngredient: RecipeMainIngredient | undefined =
    recipe[ingredientIndex - 1];

  ingredientLoaders.push(
    ...getBookableTransportLoaderSegments(
      previousIngredient,
      getFlightAllCarriers,
      getFerryAllCarriers,
      currency
    )
  );

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.DateLocation,
    location: getLocationSegment(ingredient.displayText),
    numberOfNights: ingredient.numberOfNights,
    ingredientId: ingredient.ingredientId,
  });

  if (ingredient.numberOfNights !== 0) {
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.OwnStay,
      addAccommodationMessage: `${t(
        'planner.no_availability.actions.explore_hotels_selection_in'
      )} ${getCityText(ingredient.displayText)}`,
      context: {
        ingredientId: ingredient.ingredientId,
        ingredientIndex,
      },
    });
  }

  ingredient.subIngredients.forEach((subIngredient) =>
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.Activity,
      barType: undefined,
      ingredientId: subIngredient.ingredientId,
      ingredientIndex,
      destination: getCityText(ingredient.displayText),
    })
  );

  if (
    !ingredientLoaders.some(
      (loader) => loader.segmentType === ItinerarySegmentType.Activity
    )
  ) {
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.Upsell,
      ingredientId: ingredient.ingredientId,
      destination: getCityText(ingredient.displayText),
    });
  }

  return ingredientLoaders;
}

function getBookableTransportLoaderSegments(
  previousIngredient: RecipeMainIngredient | undefined,
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  currency: string
): ItineraryLoaderSegment[] {
  const bookableTransportLoaderSegments: ItineraryLoaderSegment[] = [];

  const isPreviousIngredientBookableTransport =
    previousIngredient && isBookableTransportIngredient(previousIngredient);

  if (isPreviousIngredientBookableTransport) {
    const productCardTopBarType =
      getTransportProductTopBarType(previousIngredient);

    let suggestionType: SuggestionType | undefined = undefined;
    if (productCardTopBarType !== ProductCardTopBarType.Selected) {
      suggestionType =
        productCardTopBarType === ProductCardTopBarType.Suggestion
          ? SuggestionType.MostPopular
          : SuggestionType.Cheapest;
    }

    if (isFlightIngredient(previousIngredient)) {
      const filtersPills = getFlightFilterPills(
        previousIngredient.filters,
        getFlightAllCarriers(previousIngredient.ingredientId),
        currency
      );

      bookableTransportLoaderSegments.push({
        segmentType: ItinerarySegmentType.Flight,
        ingredientId: previousIngredient.ingredientId,
        barType: productCardTopBarType,
        filtersPills,
        suggestionType,
      });
    } else {
      const filtersPills = getFerryFilterPills(
        previousIngredient.filters,
        getFerryAllCarriers(previousIngredient.ingredientId)
      );

      bookableTransportLoaderSegments.push({
        segmentType: ItinerarySegmentType.Ferry,
        ingredientId: previousIngredient.ingredientId,
        barType: productCardTopBarType,
        filtersPills,
        suggestionType,
      });
    }
  }

  return bookableTransportLoaderSegments;
}

function getSimpleTransportLoaderSegments(
  ingredient: OwnTransportIngredient,
  previousIngredient: RecipeMainIngredient | undefined,
  nextIngredientDestination: string | undefined,
  recipe: RecipeMainIngredient[]
):
  | ItinerarySimpleTransportLoaderSegment
  | ItineraryNoTransportLoaderSegment
  | undefined {
  const isPreviousIngredientStay =
    previousIngredient && isStayIngredient(previousIngredient);

  const isLastSegment = ingredient.ingredientId === recipe.at(-1)?.ingredientId;
  if (isPreviousIngredientStay || !previousIngredient) {
    if (ingredient.ownTransportType !== TransportType.None) {
      return {
        segmentType: getItinerarySegmentTypeFromTransportType(
          ingredient.ownTransportType
        ),
        ingredientId: ingredient.ingredientId,
        isLastSegment: isLastSegment ? isLastSegment : undefined,
      };
    } else {
      const previousIngredientDestination = previousIngredient
        ? getCityText(previousIngredient.displayText)
        : undefined;
      return {
        segmentType: ItinerarySegmentType.NoTransport,
        ingredientId: ingredient.ingredientId,
        isReturnTransport: isLastSegment,
        city: isLastSegment
          ? previousIngredientDestination
          : nextIngredientDestination,
      };
    }
  }
}

function getHotelDateLocationLoaderLocationSegment(
  ingredient: HotelIngredient
): LocationSegment {
  const polygonText =
    ingredient.commonPolygon?.name ||
    ingredient.nearbyPolygon?.name ||
    ingredient.displayText ||
    t('planner.your_destination');

  return getLocationSegment(polygonText);
}

function getTransportUpsellLoaderSegments(
  ingredient: StayIngredient,
  ownTransportIngredient: OwnTransportIngredient
): ItineraryUpsellNoTransportSegment {
  return {
    segmentType: ItinerarySegmentType.UpsellNoTransport,
    ingredientId: ownTransportIngredient.ingredientId,
    destination: getCountryText(
      ingredient.displayText,
      ingredient.searchTerm.type === SearchTermType.FreeText
        ? ingredient.searchTerm.freeText
        : undefined
    ),
    city: getCityText(
      ingredient.displayText,
      ingredient.searchTerm.type === SearchTermType.FreeText
        ? ingredient.searchTerm.freeText
        : undefined
    ),
  };
}

export function getFlightOnlyItineraryLoaderSegments(
  recipe: RecipeMainIngredient[],
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  currency: string
): ItineraryLoaderSegment[] {
  const loaderSegments: ItineraryLoaderSegment[] = [];
  recipe.forEach((ingredient, index) => {
    if (isFlightIngredient(ingredient)) {
      const productCardTopBarType = getTransportProductTopBarType(ingredient);
      const suggestionType =
        productCardTopBarType === ProductCardTopBarType.Suggestion
          ? SuggestionType.MostPopular
          : SuggestionType.Cheapest;

      const filtersPills = getFlightFilterPills(
        ingredient.filters,
        getFlightAllCarriers(ingredient.ingredientId),
        currency
      );

      const flightLoader: ItineraryFlightLoaderSegment = {
        segmentType: ItinerarySegmentType.Flight,
        ingredientId: ingredient.ingredientId,
        isLastSegment: true,
        barType: productCardTopBarType,
        filtersPills,
        suggestionType,
      };

      loaderSegments.push(flightLoader);
    }

    if (isOwnStayIngredient(ingredient)) {
      const upsellSegment: ItineraryAccommodationUpsellLoaderSegment = {
        segmentType: ItinerarySegmentType.AccommodationUpsellLoader,
        context: {
          ingredientId: ingredient.ingredientId,
        },
      };

      loaderSegments.push(upsellSegment);
    }

    if (isUnbookableTransportIngredient(ingredient)) {
      const nextDestination = recipe[index + 1];
      const nextDestinationCity =
        nextDestination && isStayIngredient(nextDestination)
          ? nextDestination.displayText
          : undefined;
      const simpleTransportLoader = getSimpleTransportLoaderSegments(
        ingredient,
        recipe[index - 1],
        nextDestinationCity,
        recipe
      );

      if (simpleTransportLoader) {
        loaderSegments.push(simpleTransportLoader);
      }
    }
  });

  return loaderSegments;
}
